/**
 * K3 — view-only role utilities.
 *
 * Frontend mirror of the backend write-block middleware. Components use
 *   const isViewOnly = useIsViewOnly();
 * to know whether they should hide action buttons. The visible behaviour
 * is a UX courtesy — the actual block is the server-side middleware in
 * backend/middleware/viewOnly.js, so even if a button is wrongly rendered
 * and clicked, the API will return 403.
 *
 * Convention used across the codebase:
 *   {!isViewOnly && (<button ...>Save</button>)}
 *
 * For long lists of buttons we expose a tiny `<ViewOnlyHide>` wrapper that
 * collapses to null when the role is read-only, keeping JSX clean.
 */
import { Eye } from 'lucide-react';
import { useAuth } from '../store/auth';

export function useIsViewOnly() {
    const role = useAuth(s => s.user?.role);
    return role === 'view_only';
}

/** Render-only wrapper. `<ViewOnlyHide>...</ViewOnlyHide>` returns null
 *  for view-only users and its children otherwise. Use for any block of
 *  action UI you want to hide en masse. */
export function ViewOnlyHide({ children }) {
    const isViewOnly = useIsViewOnly();
    if (isViewOnly) return null;
    return children;
}

/** Small indicator pill — drop on any page header so the user understands
 *  why nothing's clickable. */
export function ViewOnlyBadge({ className = '' }) {
    const isViewOnly = useIsViewOnly();
    if (!isViewOnly) return null;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] uppercase tracking-wide font-bold bg-slate-100 text-slate-700 border border-slate-200 ${className}`}>
            <Eye className="h-3 w-3" /> View only
        </span>
    );
}
